import { useEffect, useState } from 'react';
import BaseModal from 'flow/components/ui-components/BaseModal';
import useAppContext from 'flow/hooks/useAppContext';
import { ExtensionKey, QueueKey } from 'flow/types/EntityKeys';
import InputField from '../ui-components/InputField';
import SettingSection from '../Setting/SettingSection';
import { MiniAudioPlayer } from '../AudioPlayers';
import useVoicemail from 'flow/hooks/useVoicemail';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { TypedMap } from 'flow/types/TypedMap';
import { Sound } from 'flow/types/Sound';
import Checkbox from '../ui-components/Checkbox';
import { fetchUserSounds } from 'flow/actions/sounds-actions';
import {
    createProfileTW,
    fetchReferalSounds,
} from 'flow/actions/profile-actions';
import NumberField from 'flow/components/custom-components/NumberFieldSmart';
import { Contact } from 'flow/types/Contact';
import useContact from 'flow/hooks/useContact';
import { getCountryCode } from 'flow/utils/extension-utils';
import { fromJS } from 'immutable';
import { PriorityItems } from 'flow/utils/profile-utils';
import { convertNumberToE164 } from 'flow/utils/call-utils';
import { fetchSharedExtensions } from 'flow/actions/pbx-actions';
import DropdownMenuComponent from '../ui-components/DropdownMenuComponent';
import { Extension } from 'flow/types/Extension';

interface Props {
    extensionKey?: ExtensionKey;
}

const AddAiMode = ({ extensionKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { contact } = useContact(extensionKey);
    const userCountryCode = getCountryCode(contact, null);

    const { voicemailSettings } = useVoicemail(extensionKey);
    const allQueues = useAppSelector((state) => state.pbx.get('queues'));
    const voicemailsQueues = useAppSelector((state) =>
        state.pbx.get('voicemails'),
    );
    const IVRS = useAppSelector((state) => state.pbx.get('refers'));
    const userSounds = useAppSelector((state) => state.sounds.get('sounds'));
    const extension = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );
    const standardSoundDTO = voicemailSettings?.get('standardVoicemailSound');
    const ownRecordingFiles = voicemailSettings?.get('busySoundFile');

    const availableSounds = [
        ...(standardSoundDTO ? [standardSoundDTO] : []),
        ...(userSounds?.size > 0 ? userSounds.toArray() : []),
        ...(ownRecordingFiles?.size > 0 ? ownRecordingFiles.toArray() : []),
    ] as TypedMap<Sound>[];

    const [selectedQueue, setSelectedQueue] = useState<QueueKey[]>([]);
    const [hasCallRecord, setHasCallRecord] = useState(false);
    const [hasCoListening, setHasCoListening] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);

    const calendarPrioritys: Record<string, string> = {
        25: i18next.t('settings.profile.priority.HIGH_PRIORITY'),
        50: i18next.t('settings.profile.priority.STANDARD_PRIORITY'),
        75: i18next.t('settings.profile.priority.LOW_PRIORITY'),
    };

    const statuses = {
        online: i18next.t('status.available'),
        offline: i18next.t('status.unavailable'),
    };

    const referrals = {
        sound: i18next.t('aicoworker.play_and_hangup'),
        queue: i18next.t('aicoworker.route_to_queuing_system'),
        voicemail: i18next.t('aicoworker.route_to_shared_voicemail'),
        ivr: i18next.t('aicoworker.route_to_ivr'),
        colleague: i18next.t('aicoworker.route_to_colleague'),
        external: i18next.t('aicoworker.route_to_external_number'),
    };

    const [profileName, setProfileName] = useState<string>('');
    const [status, setStatus] = useState<keyof typeof statuses>('online');
    const [calendarPriority, setCalendarPriority] =
        useState<keyof typeof calendarPrioritys>('50');
    const [referral, setReferral] = useState<keyof typeof referrals>('sound');
    const [sound, setSound] = useState<TypedMap<Sound> | null>(null);
    const [selectedReferralQueue, setSelectedReferralQueue] =
        useState<QueueKey | null>(null);
    const [selectedVoicemailQueue, setSelectedVoicemailQueue] = useState<
        string | null
    >(null);
    const [selectedIVR, setSelectedIVR] = useState<string | null>(null);
    const [selectedExtension, setSelectedExtension] =
        useState<ExtensionKey | null>(null);
    const [selectedExternalNumber, setSelectedExternalNumber] = useState<
        string | null
    >(null);

    const setSoundByKey = (key: string) => {
        const selectedSound = availableSounds.find(
            (sound) => sound.get('key') === key,
        );
        if (selectedSound) {
            setSound(selectedSound);
        }
    };

    const handleChange = async (key: QueueKey, value: boolean) => {
        setSelectedQueue((prev) => {
            if (value) {
                if (!prev?.includes(key)) {
                    return [...(prev ?? []), key];
                }
                return prev ?? [];
            }
            return (prev ?? []).filter((k) => k !== key);
        });
    };

    useEffect(() => {
        dispatch(fetchUserSounds());
        dispatch(fetchReferalSounds(extensionKey));
        dispatch(fetchSharedExtensions());
    }, []);

    const getContactName = (contact: TypedMap<Contact>) => {
        if (!contact) {
            return i18next.t('Unknown');
        }
        return (
            `${contact.get('firstName', '')} ${contact.get('lastName', '')}`.trim() ||
            i18next.t('Unknown')
        );
    };

    const createProfile = () => {
        let profileDTO = fromJS({
            description: profileName,
            available: statuses[status] === 'online',
            defaultDuration: '0',
            defaultProfile: false,
            pauseProfile: false,
            priority: PriorityItems[50].key,
            recordCalls: hasCallRecord,
            canBeCoMonitored: hasCoListening,
            callMVoip: false,
            queueLoginDTO: selectedQueue?.map((queue) => ({
                queueEntityKey: queue,
                queueDescription: allQueues.getIn([queue, 'description']),
                loggedIn: true,
            })),
        });

        if (referral === 'sound') {
            profileDTO = profileDTO.setIn(
                ['spokenNotification', 'referActionType'],
                'hangup',
            );
            profileDTO = profileDTO.setIn(
                ['spokenNotification', 'soundKey'],
                sound?.get('key') ?? '',
            );
        } else {
            profileDTO = profileDTO.setIn(
                ['callForwarding', 'unconditionalRedirect', 'type'],
                'number',
            );

            let value;
            if (referral === 'queue') {
                value = allQueues
                    .getIn([selectedReferralQueue, 'number'])
                    .toJS();
            } else if (referral === 'voicemail') {
                value = voicemailsQueues
                    .getIn([selectedVoicemailQueue, 'number'])
                    .toJS();
            } else if (referral === 'ivr') {
                value = IVRS.getIn([selectedIVR, 'number']).toJS();
            } else if (referral === 'colleague') {
                value = extension
                    .getIn([selectedExtension, 'contact', 'fixed'])
                    .toJS();
            } else if (referral === 'external') {
                value = convertNumberToE164(
                    selectedExternalNumber,
                    userCountryCode,
                );
            }
            profileDTO = profileDTO.setIn(
                ['callForwarding', 'unconditionalRedirect', 'forwardingNumber'],
                value,
            );
        }

        dispatch(createProfileTW(extensionKey, profileDTO));
    };

    useEffect(() => {
        if (
            profileName === '' ||
            (referral === 'queue' && !selectedReferralQueue) ||
            (referral === 'voicemail' && !selectedVoicemailQueue) ||
            (referral === 'ivr' && !selectedIVR) ||
            (referral === 'colleague' && !selectedExtension) ||
            (referral === 'external' && !selectedExternalNumber) ||
            (referral === 'sound' && !sound)
        ) {
            setDisableSubmit(true);
        }
    }, [
        referral,
        profileName,
        selectedReferralQueue,
        selectedVoicemailQueue,
        selectedIVR,
        selectedExtension,
        selectedExternalNumber,
        sound,
    ]);

    return (
        <BaseModal
            open={true}
            header={i18next.t('aicoworker.create_new_mode')}
            onSubmit={createProfile}
            disableSubmit={disableSubmit}
        >
            <div className="flex flex-col gap-4">
                <SettingSection className="px-0">
                    {i18next.t('settings.general')}
                </SettingSection>

                <InputField
                    placeholder={i18next.t('settings.profile.name_field_hint')}
                    value={profileName}
                    label={i18next.t('pbx.name')}
                    onChange={(e) => setProfileName(e.target.value)}
                />

                <DropdownMenuComponent
                    title={statuses[status]}
                    label={i18next.t('status.status')}
                    onItemSelected={(value: keyof typeof statuses) =>
                        setStatus(value)
                    }
                    options={Object.keys(statuses).map(
                        (key: keyof typeof statuses) => ({
                            key,
                            label: statuses[key],
                        }),
                    )}
                />
                <DropdownMenuComponent
                    title={calendarPrioritys[calendarPriority]}
                    label={i18next.t('aicoworker.calendar_priority')}
                    onItemSelected={setCalendarPriority}
                    options={Object.keys(calendarPrioritys).map((key) => ({
                        key,
                        label: calendarPrioritys[key],
                    }))}
                />

                <SettingSection className="mt-8 px-0">
                    {i18next.t('settings.incoming_calls')}
                </SettingSection>
                <DropdownMenuComponent
                    title={referrals[referral]}
                    label={i18next.t('aicoworker.behaviour')}
                    onItemSelected={(value: keyof typeof referrals) =>
                        setReferral(value)
                    }
                    options={Object.keys(referrals).map(
                        (key: keyof typeof referrals) => ({
                            key,
                            label: referrals[key],
                        }),
                    )}
                />
                <div>
                    {referral === 'sound' && (
                        <div className="flex items-end gap-x-2">
                            <div className="grow">
                                <DropdownMenuComponent
                                    title={i18next.t(
                                        `${sound?.get('name') ?? ''}`,
                                    )}
                                    label={i18next.t('pbx.sound')}
                                    onItemSelected={setSoundByKey}
                                    options={availableSounds.map((sound) => ({
                                        key: sound.get('key'),
                                        label: sound.get('name'),
                                    }))}
                                />
                            </div>
                            <div className="bg-gray-200">
                                <MiniAudioPlayer
                                    url={
                                        sound?.get('playbackURL')
                                            ? `/${sound?.get('playbackURL')}`
                                            : null
                                    }
                                    uniqueId={sound?.get('key') ?? ''}
                                />
                            </div>
                        </div>
                    )}
                    {referral === 'queue' && (
                        <DropdownMenuComponent
                            title={
                                allQueues.getIn([
                                    selectedReferralQueue,
                                    'description',
                                ]) ??
                                i18next.t('ai-coworker.settings.select.queue')
                            }
                            label={i18next.t('Queue.Queue')}
                            onItemSelected={(value: QueueKey) =>
                                setSelectedReferralQueue(value)
                            }
                            options={allQueues
                                .valueSeq()
                                .map<any>((val, key) => ({
                                    key: key,
                                    label: val.get('description'),
                                }))}
                        />
                    )}
                    {referral === 'voicemail' && (
                        <DropdownMenuComponent
                            title={
                                voicemailsQueues.getIn([
                                    selectedVoicemailQueue,
                                    'description',
                                ]) ?? i18next.t('Select queue')
                            }
                            label={i18next.t('settings.voicemail_shared')}
                            onItemSelected={setSelectedVoicemailQueue}
                            options={voicemailsQueues
                                .valueSeq()
                                .map<any>((val, key) => ({
                                    key: key,
                                    label: val.get('description'),
                                }))}
                        />
                    )}
                    {referral === 'ivr' && (
                        <DropdownMenuComponent
                            title={
                                IVRS.getIn([selectedIVR, 'description']) ??
                                i18next.t('ai-coworker.settings.select.IVR')
                            }
                            label={i18next.t('pbx.refers')}
                            onItemSelected={setSelectedIVR}
                            options={IVRS.valueSeq().map<any>((val, key) => ({
                                key: key,
                                label: val.get('description'),
                            }))}
                        />
                    )}
                    {referral === 'colleague' && (
                        <DropdownMenuComponent
                            title={
                                getContactName(
                                    extension.getIn([
                                        selectedExtension,
                                        'contact',
                                    ]),
                                ) ??
                                i18next.t(
                                    'ai-coworker.settings.select.colleague',
                                )
                            }
                            label={i18next.t(
                                'ai-coworker.settings.select.colleague',
                            )}
                            onItemSelected={(value: ExtensionKey) =>
                                setSelectedExtension(value)
                            }
                            options={extension
                                .valueSeq()
                                .map<any>((q: TypedMap<Extension>) => ({
                                    key: q.get('key'),
                                    label: getContactName(q.get('contact')),
                                }))}
                        />
                    )}

                    {referral === 'external' && (
                        <NumberField
                            key="anumber-numberfield"
                            id="fixed"
                            numberDTO={null}
                            onChange={setSelectedExternalNumber}
                            label={i18next.t('aicoworker.external_number')}
                            disabled={false}
                            defaultCountryCode={userCountryCode}
                        />
                    )}
                </div>

                <SettingSection className="mt-8 px-0">
                    {i18next.t('ai-coworker.settings.queuing-system-login')}
                </SettingSection>

                {allQueues.valueSeq().map((queue) => (
                    <Checkbox
                        key={queue.get('key')}
                        label={queue.get('description')}
                        checked={selectedQueue?.includes(queue.get('key'))}
                        onChange={(event, value) =>
                            handleChange(queue.get('key'), value)
                        }
                    />
                ))}
                <SettingSection className="mt-8 px-0">
                    {i18next.t('aicoworker.call_recording_and_co_listening')}
                </SettingSection>

                <Checkbox
                    label={i18next.t('settings.allow_call_monitoring')}
                    checked={hasCallRecord}
                    onChange={() => setHasCallRecord((prev) => !prev)}
                />
                <Checkbox
                    label={i18next.t('settings.record_calls')}
                    checked={hasCoListening}
                    onChange={() => setHasCoListening((prev) => !prev)}
                />
            </div>
        </BaseModal>
    );
};

export default AddAiMode;
